import { LocalDate } from "@js-joda/core";
import type { CompanyCustomerInvoiceRow } from "model/Company";
import { useRef, useState } from "react";
import { Api } from "services/api/api.service";

type GetCustomerInvoiceParams = {
  companyId?: number;
  selectedDates: readonly LocalDate[];
  selectedProductIds: readonly string[];
};

type UseCustomerInvoiceDataResult = {
  getInvoice(params: GetCustomerInvoiceParams): Promise<boolean>;
  hasError: boolean;
  hasFetchedInvoice: boolean;
  invoiceRows: readonly CompanyCustomerInvoiceRow[];
  isLoadingInvoice: boolean;
  resetInvoice(): void;
};

const toUtcDate = (date: LocalDate): Date =>
  new Date(Date.UTC(date.year(), date.monthValue() - 1, date.dayOfMonth()));

const filterAndSortInvoiceRows = (
  invoiceRows: readonly CompanyCustomerInvoiceRow[],
): readonly CompanyCustomerInvoiceRow[] =>
  [...invoiceRows]
    .filter((invoiceRow) => invoiceRow.totalWorkHours > 0)
    .sort((leftRow, rightRow) =>
      leftRow.product.localeCompare(rightRow.product),
    );

export const useCustomerInvoiceData = (): UseCustomerInvoiceDataResult => {
  const activeRequestIdRef = useRef(0);
  const [hasError, setHasError] = useState(false);
  const [hasFetchedInvoice, setHasFetchedInvoice] = useState(false);
  const [invoiceRows, setInvoiceRows] = useState<
    readonly CompanyCustomerInvoiceRow[]
  >([]);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);

  const resetInvoice = (): void => {
    activeRequestIdRef.current += 1;
    setHasError(false);
    setHasFetchedInvoice(false);
    setInvoiceRows([]);
    setIsLoadingInvoice(false);
  };

  const getInvoice = async ({
    companyId,
    selectedDates,
    selectedProductIds,
  }: GetCustomerInvoiceParams): Promise<boolean> => {
    if (selectedDates.length === 0 || companyId === undefined) {
      return false;
    }

    const startDate = selectedDates[0];
    const endDate = selectedDates[selectedDates.length - 1];

    if (!startDate || !endDate) {
      return false;
    }

    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;
    setHasError(false);
    setIsLoadingInvoice(true);

    try {
      const response = await Api().company.getInvoiceInformationForPeriod(
        toUtcDate(startDate),
        toUtcDate(endDate),
        [...selectedProductIds],
        companyId,
      );

      if (activeRequestIdRef.current !== requestId) {
        return false;
      }

      setInvoiceRows(filterAndSortInvoiceRows(response.data));
      setHasFetchedInvoice(true);
      return true;
    } catch {
      if (activeRequestIdRef.current !== requestId) {
        return false;
      }

      setHasError(true);
      return false;
    } finally {
      if (activeRequestIdRef.current === requestId) {
        setIsLoadingInvoice(false);
      }
    }
  };

  return {
    getInvoice,
    hasError,
    hasFetchedInvoice,
    invoiceRows,
    isLoadingInvoice,
    resetInvoice,
  };
};
